import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { makeT, normalizeLang } from "@/lib/i18n";
import { UPLOAD_STRINGS } from "@/lib/uploadI18n";
import { useAuth } from "@/context/AuthContext";

// Upload.jsx still consumes legacy credit-named translation keys while the
// backend/session UI migrates to the daily-session model. Override only the
// user-facing copy here so no credit terminology leaks into this flow.
const SESSION_LIMIT_COPY = {
  en: {
    errInsufficientCredits: "You’ve used all interview sessions available today. Your session allowance resets at midnight.",
    noCreditsTitle: "Daily interview session limit reached",
    noCreditsBody: "You’ve used all interview sessions available today. Your session allowance resets at midnight.",
    creditsBalance: "Interview sessions",
  },
  "pt-BR": {
    errInsufficientCredits: "Você já usou todas as sessões de entrevista disponíveis hoje. Seu limite de sessões é renovado à meia-noite.",
    noCreditsTitle: "Limite diário de sessões de entrevista atingido",
    noCreditsBody: "Você já usou todas as sessões de entrevista disponíveis hoje. Seu limite de sessões é renovado à meia-noite.",
    creditsBalance: "Sessões de entrevista",
  },
  "es-419": {
    errInsufficientCredits: "Ya utilizó todas las sesiones de entrevista disponibles hoy. Su límite de sesiones se restablece a medianoche.",
    noCreditsTitle: "Límite diario de sesiones de entrevista alcanzado",
    noCreditsBody: "Ya utilizó todas las sesiones de entrevista disponibles hoy. Su límite de sesiones se restablece a medianoche.",
    creditsBalance: "Sesiones de entrevista",
  },
  "fr-CA": {
    errInsufficientCredits: "Vous avez utilisé toutes les sessions d’entrevue disponibles aujourd’hui. Votre allocation de sessions est réinitialisée à minuit.",
    noCreditsTitle: "Limite quotidienne de sessions d’entrevue atteinte",
    noCreditsBody: "Vous avez utilisé toutes les sessions d’entrevue disponibles aujourd’hui. Votre allocation de sessions est réinitialisée à minuit.",
    creditsBalance: "Sessions d’entrevue",
  },
};

for (const [code, strings] of Object.entries(SESSION_LIMIT_COPY)) {
  if (UPLOAD_STRINGS[code]) Object.assign(UPLOAD_STRINGS[code], strings);
}

export default function SessionAllowanceTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);

  const visible = useMemo(() => {
    const path = location.pathname;
    return path === "/" || path === "/upload" || /^\/interview\/[^/]+$/.test(path);
  }, [location.pathname]);

  const lang = normalizeLang(user?.preferred_language);
  const t = makeT(lang);

  useEffect(() => {
    if (!visible || !user || user?.is_admin) {
      setUsage(null);
      return undefined;
    }

    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/session-allowance");
        if (alive) setUsage(data || null);
      } catch {
        if (alive) setUsage(null);
      }
    };

    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [visible, user?.user_id, user?.is_admin, location.pathname]);

  if (!visible || !usage || user?.is_admin || usage?.limit_disabled) return null;

  const atLimit = Number(usage.used) >= Number(usage.limit);
  const label = atLimit
    ? t("sessions.limitTracker", usage.used, usage.limit)
    : t("sessions.today", usage.used, usage.limit);

  return (
    <div
      data-testid="session-allowance-tracker"
      role="status"
      aria-live="polite"
      className="fixed top-4 right-4 z-50 rounded-full border border-[color:var(--border-stone)] bg-white/95 px-4 py-2 text-xs font-medium text-[color:var(--text-secondary)] shadow-sm backdrop-blur"
    >
      {label}
    </div>
  );
}
